import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import api from "@api";
import imageCompression from "browser-image-compression";
import "./LeaguePlayersAuth.css";

function LeaguePlayersAuth({
  seasonId,
  leagueId,
  allPlayers,
  previewImage,
  setPreviewImage,
  setMessage,
}) {
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [addPlayerStep, setAddPlayerStep] = useState("names");
  const [bulkPlayerRows, setBulkPlayerRows] = useState([
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
  ]);
  const [playerReviewList, setPlayerReviewList] = useState([]);
  const [isCheckingPlayers, setIsCheckingPlayers] = useState(false);
  const [isSavingPlayers, setIsSavingPlayers] = useState(false);
  const [addPlayerError, setAddPlayerError] = useState("");

  const [isEditPlayerModalOpen, setIsEditPlayerModalOpen] = useState(false);
  const [editPlayerForm, setEditPlayerForm] = useState({
    id: null,
    firstName: "",
    lastName: "",
    image: null,
    existingImage: "",
  });
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [individualPlayers, setIndividualPlayers] = useState([]);

  const fetchIndividualPlayers = async () => {
    if (!seasonId || !leagueId) return;

    try {
      const res = await api.get(
        `/api/seasonsv2/${seasonId}/leagues/${leagueId}/players`,
        { withCredentials: true }
      );

      setIndividualPlayers(res.data.players || []);
    } catch (err) {
      console.error("Error fetching individual players:", err);
    }
  };

  useEffect(() => {
    fetchIndividualPlayers();
  }, [seasonId, leagueId]);

  const normalizeName = (name) =>
    (name || "").toLowerCase().replace(/\s+/g, " ").trim();

  const getPlayerFullName = (player) =>
    `${player.firstName || ""} ${player.lastName || ""}`.trim();

  const splitPlayerName = (fullName) => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);

    return {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
    };
  };

  const getIndividualPlayerName = (entry) => {
    const player = entry.player || entry;

    return `${player.firstName || ""} ${player.lastName || ""}`.trim();
  };

  const getEnteredPlayerRows = () => {
    return bulkPlayerRows
      .map((row) => ({
        firstName: row.firstName.replace(/\s+/g, " ").trim(),
        lastName: row.lastName.replace(/\s+/g, " ").trim(),
      }))
      .filter((row) => row.firstName || row.lastName);
  };

  const getDuplicateEnteredNames = (rows) => {
    const seen = new Set();
    const duplicates = new Set();

    rows.forEach((row) => {
      const name = `${row.firstName} ${row.lastName}`.trim();
      const key = normalizeName(name);

      if (seen.has(key)) {
        duplicates.add(name);
      }

      seen.add(key);
    });

    return [...duplicates];
  };

  const getAlreadyInLeagueNames = (rows) => {
    const existingNames = individualPlayers.map((entry) =>
      normalizeName(getIndividualPlayerName(entry))
    );

    return rows
      .map((row) => `${row.firstName} ${row.lastName}`.trim())
      .filter((name) => existingNames.includes(normalizeName(name)));
  };

  const getPlayerHistory = (player) => {
    const rosters = player.rosters || [];

    const history = rosters
      .map((roster) => {
        const slt = roster.seasons_leagues_team || {};
        const seasonLeague = slt.seasons_league || {};

        const teamName =
          roster.team?.name ||
          slt.team?.name ||
          "Unknown Team";

        const leagueName =
          roster.league?.name ||
          seasonLeague.league?.name ||
          "Unknown League";

        const seasonName =
          seasonLeague.season?.name ||
          roster.season?.name ||
          "Unknown Season";

        return `${teamName} — ${leagueName} — ${seasonName}`;
      })
      .filter(Boolean);

    return [...new Set(history)];
  };

  const findPotentialMatches = (typedName, playersToSearch) => {
    const normalizedTypedName = normalizeName(typedName);
    const { firstName, lastName } = splitPlayerName(typedName);

    const normalizedFirst = normalizeName(firstName);
    const normalizedLast = normalizeName(lastName);

    const exactMatches = playersToSearch.filter((player) => {
      const playerName = normalizeName(getPlayerFullName(player));
      return playerName === normalizedTypedName;
    });

    if (exactMatches.length > 0) return exactMatches.slice(0, 5);

    const possibleMatches = playersToSearch.filter((player) => {
      const playerFirst = normalizeName(player.firstName);
      const playerLast = normalizeName(player.lastName);
      const playerFull = normalizeName(getPlayerFullName(player));

      if (!normalizedTypedName) return false;

      if (normalizedFirst && normalizedLast) {
        return (
          playerFirst === normalizedFirst &&
          playerLast === normalizedLast
        );
      }

      return (
        playerFirst === normalizedTypedName ||
        playerLast === normalizedTypedName ||
        playerFull.includes(normalizedTypedName)
      );
    });

    return possibleMatches.slice(0, 5);
  };

  const resetAddPlayersModal = () => {
    setBulkPlayerRows([
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
    ]);
    setPlayerReviewList([]);
    setAddPlayerStep("names");
    setAddPlayerError("");
    setIsCheckingPlayers(false);
    setIsSavingPlayers(false);
  };

  const handleOpenAddPlayerModal = () => {
    resetAddPlayersModal();
    setIsPlayerModalOpen(true);
  };

  const handleBulkPlayerRowChange = (index, field, value) => {
    setBulkPlayerRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const handleAddBulkPlayerRow = () => {
    setBulkPlayerRows((prev) => [
      ...prev,
      { firstName: "", lastName: "" },
    ]);
  };

  const handleRemoveBulkPlayerRow = (index) => {
    setBulkPlayerRows((prev) =>
      prev.length === 1
        ? [{ firstName: "", lastName: "" }]
        : prev.filter((_, rowIndex) => rowIndex !== index)
    );
  };

  const handleCheckPlayers = () => {
    const rows = getEnteredPlayerRows();

    if (!rows.length) {
      setAddPlayerError("Please enter at least one player name.");
      return;
    }

    const duplicateNames = getDuplicateEnteredNames(rows);

    if (duplicateNames.length > 0) {
      setAddPlayerError(
        `Duplicate player name in list: ${duplicateNames.join(", ")}`
      );
      return;
    }

    const alreadyInLeague = getAlreadyInLeagueNames(rows);

    if (alreadyInLeague.length > 0) {
      setAddPlayerError(
        `Already in this league: ${alreadyInLeague.join(", ")}`
      );
      return;
    }

    setIsCheckingPlayers(true);
    setAddPlayerError("");

    const reviewList = rows.map((row, index) => {
      const typedName = `${row.firstName} ${row.lastName}`.trim();
      const matches = findPotentialMatches(typedName, allPlayers || []);

      return {
        tempId: `${typedName}-${index}`,
        typedName,
        firstName: row.firstName,
        lastName: row.lastName,
        matches,
        action: matches.length > 0 ? "existing" : "new",
        selectedPlayerId: matches[0]?.id || "",
      };
    });

    setPlayerReviewList(reviewList);
    setAddPlayerStep("review");
    setIsCheckingPlayers(false);
  };

  const updatePlayerReviewChoice = (tempId, updates) => {
    setPlayerReviewList((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, ...updates } : item
      )
    );
  };

  const handleAddPlayersToLeague = async () => {
    if (!playerReviewList.length) {
      setAddPlayerError("Please check players first.");
      return;
    }

    for (const item of playerReviewList) {
      if (item.action === "existing" && !item.selectedPlayerId) {
        setAddPlayerError(
          `Please choose an existing player for "${item.typedName}".`
        );
        return;
      }
    }

    try {
      setIsSavingPlayers(true);
      setAddPlayerError("");

      let addedCount = 0;
      let skippedCount = 0;
      const failedPlayers = [];

      for (const item of playerReviewList) {
        try {
          const formData = new FormData();

          if (item.action === "existing" && item.selectedPlayerId) {
            formData.append("player_id", item.selectedPlayerId);
          } else {
            formData.append("firstName", item.firstName);
            formData.append("lastName", item.lastName || "");
          }

          await api.post(
            `/api/seasonsv2/${seasonId}/leagues/${leagueId}/players`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              withCredentials: true,
            }
          );

          addedCount += 1;
        } catch (err) {
          const apiMessage = err.response?.data?.message;

          if (
            apiMessage === "Player already in this league" ||
            apiMessage === "Player already added to this league"
          ) {
            skippedCount += 1;
          } else {
            failedPlayers.push(item.typedName);
            console.error(
              "Error adding individual player:",
              item.typedName,
              err.response?.data || err.message
            );
          }
        }
      }

      await fetchIndividualPlayers();

      setIsSavingPlayers(false);
      setIsPlayerModalOpen(false);
      resetAddPlayersModal();

      if (failedPlayers.length > 0) {
        setMessage(
          `Added ${addedCount} player${addedCount === 1 ? "" : "s"}. ${skippedCount} already in league. ${failedPlayers.length} failed.`
        );
      } else if (skippedCount > 0) {
        setMessage(
          `Added ${addedCount} player${addedCount === 1 ? "" : "s"}. ${skippedCount} already in league.`
        );
      } else {
        setMessage(
          `Added ${addedCount} player${addedCount === 1 ? "" : "s"} to league.`
        );
      }

      setTimeout(() => setMessage(""), 4000);
    } catch (err) {
      console.error("Error adding players to individual league:", err);
      setAddPlayerError(
        err.response?.data?.message || "Failed to add players."
      );
      setIsSavingPlayers(false);
    }
  };

  const handleEditPlayerImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });

      setEditPlayerForm((prev) => ({
        ...prev,
        image: compressed,
      }));
    } catch (err) {
      console.error("Error compressing edit player image:", err);
    }
  };

  const handleUpdateIndividualPlayer = async (e) => {
    e.preventDefault();
    if (!editPlayerForm.id) return;

    try {
      const formData = new FormData();

      formData.append("firstName", editPlayerForm.firstName);
      formData.append("lastName", editPlayerForm.lastName || "");

      if (editPlayerForm.image) {
        formData.append("image", editPlayerForm.image);
      }

      await api.put(`/api/playersv2/${editPlayerForm.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      await fetchIndividualPlayers();

      setIsEditPlayerModalOpen(false);
      setEditPlayerForm({
        id: null,
        firstName: "",
        lastName: "",
        image: null,
        existingImage: "",
      });

      setMessage("Player updated successfully");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating player:", err);
      setMessage("Failed to update player");
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const handleDeleteSelectedPlayers = async () => {
    if (!selectedPlayers.length) {
      return alert("Please select at least one player.");
    }

    const confirmRemove = window.confirm(
      "Remove selected players from this league? This will not delete their player profiles."
    );

    if (!confirmRemove) return;

    try {
      await Promise.all(
        selectedPlayers.map((playerId) =>
          api.delete(
            `/api/seasonsv2/${seasonId}/leagues/${leagueId}/players/${playerId}`,
            { withCredentials: true }
          )
        )
      );

      await fetchIndividualPlayers();

      setSelectedPlayers([]);
      setMessage("Players removed from league");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error removing selected individual players:", err);
      setMessage("Failed to remove selected players");
      setTimeout(() => setMessage(""), 3000);
    }
  };
  return (
    <div className="individualLeaguePlayersAuth-container">
      <div className="individualLeaguePlayersAuth-btn-container">
        <button
          className="individualLeaguePlayersAuth-add-btn"
          onClick={handleOpenAddPlayerModal}
        >
          + Add Players
        </button>

        <button
          type="button"
          className="individualLeaguePlayersAuth-delete-btn"
          onClick={handleDeleteSelectedPlayers}
          disabled={selectedPlayers.length === 0}
          title={
            selectedPlayers.length === 0
              ? "Select players first"
              : "Delete selected players"
          }
        >
          Delete Selected
        </button>
      </div>

      <table className="individualLeaguePlayersAuth-table">
        <thead>
          <tr>
            <th className="individualLeaguePlayersAuth-checkboxCol">
              <input
                type="checkbox"
                checked={
                  individualPlayers.length > 0 &&
                  selectedPlayers.length === individualPlayers.length
                }
                onChange={() =>
                  setSelectedPlayers(
                    selectedPlayers.length === individualPlayers.length
                      ? []
                      : individualPlayers.map((entry) => entry.player?.id).filter(Boolean)
                  )
                }
              />
            </th>
            <th>Rank</th>
            <th>Player</th>
            <th>Matches</th>
            <th>Wins</th>
            <th>Losses</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {individualPlayers.length === 0 ? (
            <tr>
              <td colSpan="7" className="individualLeaguePlayersAuth-empty">
                No players available
              </td>
            </tr>
          ) : (
            individualPlayers.map((entry, index) => (
              <tr key={entry.id}>
                <td className="individualLeaguePlayersAuth-checkboxCol">
                  <input
                    type="checkbox"
                    checked={selectedPlayers.includes(entry.player?.id)}
                    onChange={() =>
                      setSelectedPlayers((prev) =>
                        prev.includes(entry.player?.id)
                          ? prev.filter((id) => id !== entry.player?.id)
                          : [...prev, entry.player?.id]
                      )
                    }
                  />
                </td>
                <td>{index + 1}</td>

                <td>
                  <div className="individualLeaguePlayersAuth-player-cell">
                    {entry.player?.image ? (
                      <img
                        src={entry.player.image}
                        alt={`${entry.player?.firstName || "Player"} ${entry.player?.lastName || ""
                          }`}
                        className="individualLeaguePlayersAuth-player-img"
                        onClick={() => setPreviewImage(entry.player.image)}
                      />
                    ) : (
                      <div className="individualLeaguePlayersAuth-player-placeholder">
                        {(entry.player?.firstName?.[0] || "?").toUpperCase()}
                      </div>
                    )}

                    <NavLink
                      className="individualLeaguePlayersAuth-playerLink"
                      to={`/dashboard/players/${entry.player?.id}`}
                    >
                      {entry.player?.firstName} {entry.player?.lastName}
                    </NavLink>
                  </div>
                </td>

                <td>{entry.matches || 0}</td>
                <td>{entry.wins || 0}</td>
                <td>{entry.losses || 0}</td>

                <td>
                  <button
                    className="individualLeaguePlayersAuth-icon-btn"
                    onClick={() => {
                      setEditPlayerForm({
                        id: entry.player?.id,
                        firstName: entry.player?.firstName || "",
                        lastName: entry.player?.lastName || "",
                        image: null,
                        existingImage: entry.player?.image || "",
                      });

                      setIsEditPlayerModalOpen(true);
                    }}
                  >
                    ✏️
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {isPlayerModalOpen && (
        <div className="individualLeaguePlayersAuth-modal-overlay">
          <div className="individualLeaguePlayersAuth-modal">
            <div className="individualLeaguePlayersAuth-modal-header">
              <h3>
                {addPlayerStep === "names"
                  ? "Add Players to This League"
                  : "Review Players"}
              </h3>

              <button
                type="button"
                className="individualLeaguePlayersAuth-close-btn"
                onClick={() => {
                  setIsPlayerModalOpen(false);
                  resetAddPlayersModal();
                }}
              >
                ✕
              </button>
            </div>

            {addPlayerStep === "names" && (
              <div className="individualLeaguePlayersAuth-form">
                <p className="individualLeaguePlayersAuth-modalSubtitle">
                  Enter player names below. You can add multiple players at once.
                </p>

                {addPlayerError && (
                  <div className="individualLeaguePlayersAuth-alert">
                    {addPlayerError}
                  </div>
                )}

                <div className="individualLeaguePlayersAuth-playerRows">
                  {bulkPlayerRows.map((row, index) => (
                    <div
                      key={index}
                      className="individualLeaguePlayersAuth-playerRow"
                    >
                      <input
                        className="individualLeaguePlayersAuth-modalInput"
                        type="text"
                        placeholder="First Name"
                        value={row.firstName}
                        onChange={(e) =>
                          handleBulkPlayerRowChange(
                            index,
                            "firstName",
                            e.target.value
                          )
                        }
                      />

                      <input
                        className="individualLeaguePlayersAuth-modalInput"
                        type="text"
                        placeholder="Last Name"
                        value={row.lastName}
                        onChange={(e) =>
                          handleBulkPlayerRowChange(
                            index,
                            "lastName",
                            e.target.value
                          )
                        }
                      />

                      <button
                        type="button"
                        className="individualLeaguePlayersAuth-removeRowBtn"
                        onClick={() => handleRemoveBulkPlayerRow(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="individualLeaguePlayersAuth-addRowBtn"
                  onClick={handleAddBulkPlayerRow}
                >
                  + Add Another Player
                </button>

                <div className="individualLeaguePlayersAuth-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setIsPlayerModalOpen(false);
                      resetAddPlayersModal();
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={isCheckingPlayers}
                    onClick={handleCheckPlayers}
                  >
                    {isCheckingPlayers ? "Checking..." : "Check Players"}
                  </button>
                </div>
              </div>
            )}

            {addPlayerStep === "review" && (
              <div className="individualLeaguePlayersAuth-form">
                <p className="individualLeaguePlayersAuth-modalSubtitle">
                  Choose whether to use an existing player profile or create a new one.
                </p>

                {addPlayerError && (
                  <div className="individualLeaguePlayersAuth-alert">
                    {addPlayerError}
                  </div>
                )}

                <div className="individualLeaguePlayersAuth-reviewList">
                  {playerReviewList.map((item) => (
                    <div
                      key={item.tempId}
                      className="individualLeaguePlayersAuth-reviewCard"
                    >
                      <div className="individualLeaguePlayersAuth-reviewCardHeader">
                        <div>
                          <p className="individualLeaguePlayersAuth-reviewLabel">
                            Entered Name
                          </p>

                          <h3>{item.typedName}</h3>
                        </div>

                        {item.matches.length > 0 ? (
                          <span className="individualLeaguePlayersAuth-matchBadge">
                            Possible match
                          </span>
                        ) : (
                          <span className="individualLeaguePlayersAuth-newBadge">
                            No match found
                          </span>
                        )}
                      </div>

                      {item.matches.length > 0 && (
                        <div className="individualLeaguePlayersAuth-matchList">
                          <p className="individualLeaguePlayersAuth-matchTitle">
                            Existing player profiles
                          </p>

                          {item.matches.map((match) => {
                            const history = getPlayerHistory(match);
                            const isSelected =
                              item.action === "existing" &&
                              Number(item.selectedPlayerId) === Number(match.id);

                            return (
                              <button
                                key={match.id}
                                type="button"
                                className={
                                  isSelected
                                    ? "individualLeaguePlayersAuth-matchCard individualLeaguePlayersAuth-selected"
                                    : "individualLeaguePlayersAuth-matchCard"
                                }
                                onClick={() =>
                                  updatePlayerReviewChoice(item.tempId, {
                                    action: "existing",
                                    selectedPlayerId: match.id,
                                  })
                                }
                              >
                                <div className="individualLeaguePlayersAuth-matchMainRow">
                                  <strong>{getPlayerFullName(match)}</strong>

                                  <span>
                                    {isSelected ? "Selected" : "Use this player"}
                                  </span>
                                </div>

                                {history.length > 0 ? (
                                  <div className="individualLeaguePlayersAuth-historyList">
                                    {history.slice(0, 3).map((historyItem, historyIndex) => (
                                      <small key={historyIndex}>
                                        {historyItem}
                                      </small>
                                    ))}
                                  </div>
                                ) : (
                                  <small className="individualLeaguePlayersAuth-muted">
                                    No previous roster history
                                  </small>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <button
                        type="button"
                        className={
                          item.action === "new"
                            ? "individualLeaguePlayersAuth-createNewCard individualLeaguePlayersAuth-selected"
                            : "individualLeaguePlayersAuth-createNewCard"
                        }
                        onClick={() =>
                          updatePlayerReviewChoice(item.tempId, {
                            action: "new",
                            selectedPlayerId: "",
                          })
                        }
                      >
                        <div>
                          <strong>Create New Player - </strong>

                          <small>
                            This creates a fresh player profile and adds them to this individual league.
                          </small>
                        </div>

                        <span>
                          {item.action === "new" ? "Selected" : "Choose"}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>

                <div className="individualLeaguePlayersAuth-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAddPlayerStep("names");
                      setAddPlayerError("");
                    }}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsPlayerModalOpen(false);
                      resetAddPlayersModal();
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={isSavingPlayers}
                    onClick={handleAddPlayersToLeague}
                  >
                    {isSavingPlayers ? "Adding..." : "Add Players to League"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {previewImage && (
        <div
          className="individualLeaguePlayersAuth-preview-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="individualLeaguePlayersAuth-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="individualLeaguePlayersAuth-preview-close"
              onClick={() => setPreviewImage(null)}
            >
              ✕
            </button>

            <img src={previewImage} alt="Player preview" />
          </div>
        </div>
      )}

      {isEditPlayerModalOpen && (
        <div className="individualLeaguePlayersAuth-modal-overlay">
          <div className="individualLeaguePlayersAuth-modal">
            <div className="individualLeaguePlayersAuth-modal-header">
              <h3>Edit Player</h3>

              <button
                type="button"
                className="individualLeaguePlayersAuth-close-btn"
                onClick={() => setIsEditPlayerModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form
              className="individualLeaguePlayersAuth-form"
              onSubmit={handleUpdateIndividualPlayer}
            >
              <div className="individualLeaguePlayersAuth-field">
                <label>First Name</label>

                <input
                  type="text"
                  value={editPlayerForm.firstName}
                  onChange={(e) =>
                    setEditPlayerForm({
                      ...editPlayerForm,
                      firstName: e.target.value,
                    })
                  }
                />
              </div>

              <div className="individualLeaguePlayersAuth-field">
                <label>Last Name</label>

                <input
                  type="text"
                  value={editPlayerForm.lastName}
                  onChange={(e) =>
                    setEditPlayerForm({
                      ...editPlayerForm,
                      lastName: e.target.value,
                    })
                  }
                />
              </div>

              <div className="individualLeaguePlayersAuth-field">
                <label>Player Image</label>

                {editPlayerForm.existingImage && (
                  <img
                    src={editPlayerForm.existingImage}
                    alt="Current player"
                    className="individualLeaguePlayersAuth-edit-img-preview"
                  />
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditPlayerImageChange}
                />
              </div>

              <div className="individualLeaguePlayersAuth-actions">
                <button
                  type="button"
                  onClick={() => setIsEditPlayerModalOpen(false)}
                >
                  Cancel
                </button>

                <button type="submit">Update Player</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaguePlayersAuth;